import {
  FormControl,
  FormControlProps,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  InputLabelProps,
  MenuItem,
  Select,
  SelectProps,
} from '@mui/material';
import { ClearIcon } from '@mui/x-date-pickers/icons';
import { Controller, useFormContext } from 'react-hook-form';
import { CommonFormFieldProps } from 'src/components/Forms/DynamicForm/dynamic-form';

export type FormSelectProps = Omit<FormControlProps, 'required' | 'defaultValue' | 'name'> & {
  label: string;
  options: Option[];
  required?: boolean | string;
  selectProps?: Partial<SelectProps>;
  labelProps?: Partial<InputLabelProps>;
  rules?: Record<string, unknown>;
  clearable?: boolean;
  showErrorText?: boolean;
} & CommonFormFieldProps<'select', string>;

export const FormSelect = (props: FormSelectProps) => {
  const {
    name,
    label,
    required,
    selectProps,
    labelProps,
    variant = 'standard',
    options,
    children,
    rules,
    clearable,
    showErrorText,
    ...rest
  } = props;
  const { control, formState } = useFormContext();
  const errorMsg = formState.errors[name]?.message;
  const hasErrorMsg = typeof errorMsg === 'string';

  return (
    <FormControl variant={variant} {...rest} error={hasErrorMsg}>
      <Controller
        name={name}
        control={control}
        rules={{
          ...rules,
          required,
        }}
        render={({ field }) => (
          <>
            <InputLabel htmlFor={`${name}-label`} {...labelProps}>
              {label}
            </InputLabel>
            <Select
              id={`${name}-label`}
              label={label}
              variant={variant}
              {...selectProps}
              {...field}
              onChange={(...args) => {
                field.onChange(...args);
                selectProps?.onChange?.(...args);
              }}
              endAdornment={
                clearable && field.value ? (
                  <InputAdornment position="end" sx={{ mr: 2 }}>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        field.onChange('');
                      }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null
              }
            >
              {options.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </>
        )}
      />
      {!!showErrorText && !!errorMsg && <FormHelperText>{String(errorMsg)}</FormHelperText>}
    </FormControl>
  );
};
